import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart3, FileText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { AVAILABLE_REPORTS, ReportType } from "@/types/reports";
import { BackLink } from "@/components/brand/BackLink";

export default function Reports() {
  const navigate = useNavigate();

  const handleGenerateReport = (reportType: ReportType) => {
    // Navigate to specific report page based on report type
    switch (reportType) {
      case ReportType.CLIENT_EVENT_REPORT:
        navigate("/admin/reports/client-events");
        break;
      case ReportType.USER_REPORT:
        navigate("/admin/reports/user-report");
        break;
      case ReportType.PAYMENT_CHECK_REPORT:
        navigate("/admin/reports/payment-checks");
        break;
      // Add more cases as new report types are added
      default:
      // TODO: Implement navigation for other report types
    }
  };

  const getReportIcon = () => {
    // For now, all reports use the same icon
    // Later this can be customized per report type
    return FileText;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <BackLink to="/admin/dashboard">Back to Dashboard</BackLink>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <BarChart3 className="w-8 h-8 text-foreground" />
          <div>
            <h1 className="text-3xl font-bold text-foreground">Reports</h1>
            <p className="text-foreground">
              Generate and view various business reports
            </p>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {AVAILABLE_REPORTS.map((report) => {
          const IconComponent = getReportIcon();

          return (
            <Card
              key={report.type}
              className="shadow-none hover:shadow-md hover:shadow-primary/20 transition-shadow duration-200"
            >
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-lg text-foreground">
                  <IconComponent className="h-6 w-6" />
                  {report.title}
                </CardTitle>
              </CardHeader>

              <CardContent className="space-y-4">
                <CardDescription className="text-muted-foreground leading-relaxed">
                  {report.description}
                </CardDescription>

                <Button
                  onClick={() => handleGenerateReport(report.type)}
                  variant="outline"
                  className="w-full"
                  size="sm"
                >
                  Generate Report
                </Button>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {AVAILABLE_REPORTS.length === 0 && (
        <div className="text-center py-12">
          <div className="space-y-4">
            <div className="mx-auto w-16 h-16 bg-muted rounded-full flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-muted-foreground" />
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                No Reports Available
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto">
                Report functionality is being developed. Check back soon for
                available reports.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
